import { create } from 'zustand';

// Default visible columns for trailers table
const DEFAULT_VISIBLE_COLUMNS = [
  'unitNumber',
  'status',
  'type',
  'make',
  'year',
  'length',
  'vin',
  'licensePlate',
  'licensePlateState',
  'currentLoad',
  'lastInspectionDate',
  'registrationExpiration',
];

interface TrailersState {
  // Pagination
  page: number;
  limit: number;

  // Search
  search: string;

  // Table columns and filters
  visibleColumns: string[];
  columnFilters: Record<string, string>;
}

interface TrailersActions {
  // Pagination actions
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;

  // Search actions
  setSearch: (search: string) => void;

  // Column actions
  setVisibleColumns: (columns: string[]) => void;
  toggleColumn: (column: string) => void;

  // Filter actions
  setColumnFilter: (column: string, value: string) => void;
  setColumnFilters: (filters: Record<string, string>) => void;
  clearColumnFilters: () => void;

  // Utility
  resetAll: () => void;
}

type TrailersStore = TrailersState & TrailersActions;

const initialState: TrailersState = {
  page: 1,
  limit: 25,
  search: '',
  visibleColumns: DEFAULT_VISIBLE_COLUMNS,
  columnFilters: {},
};

export const useTrailersStore = create<TrailersStore>()((set) => ({
  ...initialState,

  // Pagination actions
  setPage: (page) => set({ page }),
  setLimit: (limit) => set({ limit, page: 1 }),

  // Search actions
  setSearch: (search) => set({ search }),

  // Column actions
  setVisibleColumns: (visibleColumns) => set({ visibleColumns }),
  toggleColumn: (column) =>
    set((state) => ({
      visibleColumns: state.visibleColumns.includes(column)
        ? state.visibleColumns.filter((c) => c !== column)
        : [...state.visibleColumns, column],
    })),

  // Filter actions
  setColumnFilter: (column, value) =>
    set((state) => ({
      columnFilters: { ...state.columnFilters, [column]: value },
    })),
  setColumnFilters: (columnFilters) => set({ columnFilters }),
  clearColumnFilters: () => set({ columnFilters: {} }),

  // Utility
  resetAll: () => set(initialState),
}));
